import React, { useCallback, useState } from 'react';
import { CrmActivityMeetingItem } from '../icons/CrmActivityMeetingItem.component';
import { CrmActivityCallItem } from '../icons/CrmActivityCallItem.component';
import { CrmActivityNoteItem } from '../icons/CrmActivityNoteItem.component';
import classnames from 'classnames';
import {
  ECrmActivityType,
  IActivityParticipantDirectoryEntry,
  ICrmActivity,
  ICrmActivityCategory,
} from 'services/CrmApi/types/CrmActivityTypes';
import { formatDateTimeDisplayWithoutOffset } from 'utils';
import { HtmlStringRenderer } from 'components/HtmlStringRenderer';
import AnimateHeight from 'react-animate-height';
import { SvgIcon } from 'ui/SvgIcon';
import ArrowDown from 'ui/Icons/keyboard_arrow_down.component.svg';
import { renderFullName } from 'utils/guestInformation';
import { Link } from 'ui/Link';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { CrmActivityPerson } from '../icons/CrmActivityPerson.component';
import { CrmActivityTitle } from '../icons/CrmActivityTitle.component';
import { PencilIcon } from 'ui/Icons/components/Pencil.component';
import { CloseXComponent as CloseXIcon } from 'ui/Icons';
import ConfirmationModal, { EConfirmationModalType } from 'ui/ConfirmationModal';
import { useDeleteActivity } from './useDeleteActivity';
import { useSelector } from 'react-redux';
import { getCurrentUser } from 'store/modules/auth';
import { IUser } from 'services/BackendApi';
import { CreateActivityModal } from '../CreateActivityModal/CreateActivityModal';
import { ECrmDirectoryEntryType } from 'services/CrmApi/types/DirectoryEntry';
import { useEditActivity } from './useEditActivity';
import { CrmActivityTaskItem } from '../icons/CrmActivityTaskItem.component';
import { useTask } from 'services/TaskManagementApi/queries/useTask';
import { ITaskAPI } from 'services/TaskManagementApi/types/TaskManagementResponse';
import { TaskListPriority } from 'ui/TaskListPriority';
import { TaskListStatus } from 'ui/TaskListStatus';
import { LoadingBar } from 'ui/NetworkStatusBar';

interface IExpandableArrowProps {
  isExpanded: boolean;
  setExpanded: (value: boolean) => void;
}

const ExpandableArrow: React.FC<IExpandableArrowProps> = ({ isExpanded, setExpanded }) => {
  return (
    <button
      onClick={() => setExpanded(!isExpanded)}
      className={classnames(
        'cursor-pointer flex justify-around items-center h-[24px] w-[24px] bg-transparent hover:bg-gray-20 active:bg-gray-40 border-none outline-none'
      )}
    >
      <SvgIcon
        IconComponent={ArrowDown}
        className={classnames('transition-transform w-[14px] fill-black stroke-1', {
          'rotate-180': isExpanded,
        })}
      />
    </button>
  );
};

interface IDeleteButtonProps {
  label: string;
  onClick: () => void;
}

const DeleteButton: React.FC<IDeleteButtonProps> = ({ label, onClick }) => {
  return (
    <div className="delete-action flex items-center gap-[5px] cursor-pointer select-none" onClick={onClick}>
      <CloseXIcon />
      <span className="font-pt-sans text-[15px] text-brown-100 underline capitalize">{label}</span>
    </div>
  );
};

interface IEditButtonProps {
  label: string;
  onClick: () => void;
}

const EditButton: React.FC<IEditButtonProps> = ({ label, onClick }) => {
  return (
    <div className="edit-action flex items-center gap-[5px] cursor-pointer select-none" onClick={onClick}>
      <PencilIcon />
      <span className="font-pt-sans text-[15px] text-brown-100 underline capitalize">{label}</span>
    </div>
  );
};

const renderContactLink = (contact: IActivityParticipantDirectoryEntry) => {
  return (
    <span>
      <Link to={`/crm/contact/${contact.id}/edit`} target="_blank">
        <span className="text-brown-100 underline">{contact.name}</span>
      </Link>
      {contact.relatedCompanyId ? (
        <>
          &nbsp;
          <Link to={`/crm/company/${contact.relatedCompanyId}/edit`} target="_blank">
            <span className="text-brown-100 underline">({contact.relatedCompanyName})</span>
          </Link>
        </>
      ) : null}
    </span>
  );
};

const renderContacts = (contactList: IActivityParticipantDirectoryEntry[]) => {
  return contactList.map((contact: IActivityParticipantDirectoryEntry, index: number) => (
    <React.Fragment key={contact.id}>
      {renderContactLink(contact)}
      {index < contactList.length - 1 ? ', ' : ''}
    </React.Fragment>
  ));
};

interface IHeaderProps {
  activity: ICrmActivity;
}

const Header: React.FC<IHeaderProps> = ({ activity }) => {
  const { isMobile } = useResponsive();
  const creatorFullName = renderFullName(undefined, activity.createdBy.firstName, activity.createdBy.lastName);
  const actionMapping = {
    [ECrmActivityType.CALL]: 'set a call',
    [ECrmActivityType.MEETING]: 'scheduled a meeting',
    [ECrmActivityType.NOTE]: 'added a note',
  };
  const action = actionMapping[activity.type];

  return (
    <div className="flex items-center gap-[5px]">
      {!isMobile ? <CrmActivityPerson /> : null}
      <p className="font-pt-sans text-[13px] tracking-[0.52px] text-gray-100 m-0">
        {creatorFullName} {action} to {renderContacts(activity.participantDirectoryEntries)}
      </p>
    </div>
  );
};

interface IActivityCategoriesProps {
  categories: ICrmActivityCategory[];
}

const ActivityCategories: React.FC<IActivityCategoriesProps> = ({ categories }) => {
  if (categories.length === 0) {
    return null;
  }
  return (
    <div className="activity-categories flex items-center flex-wrap gap-[5px] my-[5px]">
      {categories.map(category => {
        return (
          <div
            key={category.id}
            className="category-tag font-pt-sans text-[13px] text-black tracking-[0.52px] bg-gray-10 whitespace-nowrap p-[5px] rounded"
          >
            {category.name}
          </div>
        );
      })}
    </div>
  );
};

interface IActivityTaskTagsProps {
  task: ITaskAPI;
}

const ActivityTaskTags: React.FC<IActivityTaskTagsProps> = ({ task }) => {
  if (!task) {
    return null;
  }
  return (
    <div className="activity-task-tags flex items-center flex-wrap gap-[10px] my-[5px]">
      <Link
        target="_blank"
        internalClassName="font-pt-sans text-[15px] text-brown-100 whitespace-nowrap underline"
        className="task-id-link cursor-pointer"
        to={`/tasks/${task.id}/edit`}
      >
        {task.key}
      </Link>
      <TaskListStatus status={task.status} />
      <TaskListPriority priority={task.priority} />
    </div>
  );
};

interface IDateProps {
  date: string;
}

const DateLine: React.FC<IDateProps> = ({ date }) => {
  return (
    <p className="date font-pt-sans text-[13px] tracking-[0.52px] capitalize text-gray-100 m-0">
      {formatDateTimeDisplayWithoutOffset(date)}
    </p>
  );
};

interface IActivityItemProps {
  activity: ICrmActivity;
  isHighlighted: boolean;
  type: ECrmActivityType;
  directoryEntryType?: ECrmDirectoryEntryType;
  directoryEntryId?: string;
  onDeleteActivity: () => void;
  onEditActivity: (activity?: ICrmActivity) => void;
}

export const ActivityItem: React.FC<IActivityItemProps> = ({
  activity,
  type,
  isHighlighted,
  onDeleteActivity,
  onEditActivity,
  directoryEntryId,
  directoryEntryType,
}) => {
  const { isMobile } = useResponsive();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const currentUser = useSelector(getCurrentUser) as IUser;

  const canDelete = currentUser.uuid === activity.createdBy?.id;
  const {
    isDeleting,
    confirmationModalOpen,
    handleOpenDeleteModal,
    handleConfirmDelete,
    handleCancelDelete,
  } = useDeleteActivity(activity, onDeleteActivity);

  const canEdit = currentUser.uuid === activity.createdBy?.id;
  const { editModalOpen, handleOpenEditModal, handleCancelEdit, ref, handleEditTabTask } = useEditActivity(
    activity,
    isHighlighted,
    setIsExpanded
  );

  const isTask = !!activity.tmsTaskId;
  const shouldShowCategories = !activity.tmsTaskId;
  const { task, taskResponse } = useTask(activity.tmsTaskId, isExpanded && !!activity.tmsTaskId);
  const shouldShowTaskTags = !!activity.tmsTaskId && !!task;

  const titleClassname = classnames('font-pt-sans text-[15px] leading-[22px] text-black m-0', {
    'overflow-hidden whitespace-nowrap text-ellipsis': !isExpanded,
    'font-[700]': isExpanded,
  });
  const titleStyles: React.CSSProperties = isExpanded ? { 'overflowWrap': 'anywhere' } : {};

  const iconMapping = {
    [ECrmActivityType.CALL]: CrmActivityCallItem,
    [ECrmActivityType.MEETING]: CrmActivityMeetingItem,
    [ECrmActivityType.NOTE]: CrmActivityNoteItem,
    [ECrmActivityType.TASK]: CrmActivityTaskItem,
  };
  const Icon = iconMapping[type];
  const handleEdit = useCallback(() => {
    if (type === ECrmActivityType.TASK) {
      handleEditTabTask(activity.tmsTaskId, directoryEntryId || '');
    } else {
      handleOpenEditModal();
    }
  }, [activity.tmsTaskId, directoryEntryId]);

  return (
    <div ref={ref} className={`activity-item activity-${activity.id} p-[10px] border border-gray-20 border-solid`}>
      <div className="activity-item flex gap-[10px]">
        <div className="icon-container flex flex-col justify-center">
          <Icon />
        </div>
        <div className="vertical-line bg-gray-10 rounded min-w-[4px] max-w-[4px]"></div>
        <div className="content-container flex-1 min-w-0">
          <div className="header-line flex justify-between items-start">
            <Header activity={activity} />
            <div className="flex items-center gap-[10px] min-w-[135px]">
              {!isMobile ? <DateLine date={activity.date} /> : null}
              <ExpandableArrow isExpanded={isExpanded} setExpanded={setIsExpanded} />
            </div>
          </div>
          {isMobile ? (
            <div className="mt-[5px]">
              <DateLine date={activity.date} />
            </div>
          ) : null}
          {isExpanded && shouldShowCategories ? <ActivityCategories categories={activity.categories ?? []} /> : null}
          {isExpanded && shouldShowTaskTags ? <ActivityTaskTags task={task} /> : null}
          {isExpanded && taskResponse.isPending && isTask ? (
            <div className="mt-5">
              <LoadingBar />
            </div>
          ) : (
            <div className="flex items-center gap-[5px]">
              {!isMobile ? <CrmActivityTitle /> : null}
              <HtmlStringRenderer className={titleClassname} content={activity.title ?? ''} style={titleStyles} />
            </div>
          )}
        </div>
      </div>

      <AnimateHeight
        duration={300}
        height={isExpanded ? 'auto' : 0}
        className={classnames('accordion-area mt-[10px] pl-[48px]', {})}
      >
        <HtmlStringRenderer
          className="font-pt-sans text-[15px] leading-[22px] text-black m-0"
          content={activity.description ?? ''}
        />
        <div className="actions flex items-center gap-[20px] mt-[20px]">
          {canEdit ? <EditButton label="Edit" onClick={handleEdit} /> : null}
          {canDelete ? <DeleteButton label="Delete" onClick={handleOpenDeleteModal} /> : null}
        </div>
      </AnimateHeight>

      {confirmationModalOpen && (
        <ConfirmationModal
          type={EConfirmationModalType.WARNING}
          className="confirm-import-modal"
          title="You are going to delete this activity."
          message={`Are you sure you want to delete?`}
          confirmButtonLabel="Yes"
          cancelButtonLabel="No"
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
          isConfirmLoading={isDeleting}
        />
      )}

      {editModalOpen ? (
        <CreateActivityModal
          activity={activity}
          type={activity.type}
          directoryEntryId={directoryEntryId}
          directoryEntryType={directoryEntryType}
          onSuccess={onEditActivity}
          onClose={handleCancelEdit}
        />
      ) : null}
    </div>
  );
};
